import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { apiService } from '../../components/Config/api.service';
import { notify } from '../../components/Utils/Notifications';
import AfficheQrLieu from './AfficheQrLieu';
import CarteLieux from './CarteLieux';
import HorairesTravail from './HorairesTravail';
import HeaderCard from '../../components/Header/HeaderCard';
import LoadingBarWithStyles from '../../components/Utils/LoadingBarWithStyles';

/**
 * Configuration du contrôle de zone du pointage (contrat C13, Yros/Kavero#348).
 *
 * Les lieux sont édités en pile : un bloc par lieu, tous dépliés. Le bolt 034
 * l'a choisi sur un constat — « trois lieux de travail au plus sur `yros_sn` »
 * — donc un formulaire, pas une liste : aucun `TableList`, aucune pagination.
 *
 * **Ce constat est daté, et il va expirer.** Relevé le 2026-08-26 : une seule
 * société de `yros_sn` porte des lieux, et elle en a trois. Mais ce sont les
 * valeurs par défaut d'Odoo, pas un usage — la création depuis le portail
 * n'existait pas avant le lot C de `Yros/Kavero#369`. Dès que des clients
 * configureront leurs sites, une société à quinze agences est banale.
 *
 * **Seuil : au-delà de huit lieux, passer à un sélecteur** — choisir un lieu,
 * puis éditer celui-là — plutôt qu'à quinze blocs de quatre champs empilés
 * au-dessus de la carte.
 *
 * Ce qui portait vraiment la pile, « voir toutes ses zones d'un coup d'œil »,
 * est depuis mieux servi par la carte (lot D) que par les blocs. La bascule ne
 * coûtera donc rien de ce côté-là : c'est bien le nombre, et lui seul, qui
 * doit la déclencher.
 */

const MODES = ['strict', 'souple', 'desactive'];
// Délai de secours : l'option `timeout` de `getCurrentPosition` ne court pas
// tant que l'invite de permission n'a pas été tranchée (patron `Pointage.js`,
// bolt 030, non fusionné — code non partagé, motif au plan § Risques).
const DELAI_POSITION_MS = 8000;

const CLASSE_CHAMP = 'mt-1 w-full px-4 py-2 border rounded-lg text-gray-700 bg-gray-50 font-medium text-sm';
const CLASSE_AVERTISSEMENT = 'text-sm text-yellow-800 bg-yellow-100 rounded-lg px-3 py-2';
const CLASSE_ERREUR = 'text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2';
const CLASSE_INFO = 'text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2';
// Même borne que côté proxy et côté Odoo (`LONGUEUR_NOM_LIEU_MAX`) : la
// recopier ici évite un aller-retour pour un refus que le front sait prononcer.
const LONGUEUR_NOM_MAX = 128;

// Recopie littérale de `res_users.py:339` : un `||` puis un `&&`. Inverser
// l'un des deux ferait avertir à tort tout site posé sur l'équateur ou sur
// Greenwich. Comparaison en nombre comme `aEnvoyer`/`champsValides` plus bas
// (via `enNombre`) : l'état d'un champ édité porte la chaîne posée par
// `onChange`, jamais reconvertie avant l'enregistrement — `!"0"` vaut
// `false` en JS, contrairement au nombre 0.
const zoneNonConfiguree = (lieu) => !enNombre(lieu.rayon_m) || (!enNombre(lieu.latitude) && !enNombre(lieu.longitude));
// Avertissement seul, jamais un blocage : le front ne décide aucune règle
// métier. La précision GPS courante (35 à 50 m) produit de faux `hors_zone`
// sur un rayon plus serré ; la décision reste chez Odoo.
const rayonServre = (lieu) => enNombre(lieu.rayon_m) > 0 && enNombre(lieu.rayon_m) < 100;
// Un seul gabarit JSX pour les deux avertissements, et un pour les trois
// champs numériques d'un lieu (`rayon_m` porte un testid `rayon`, sans le
// suffixe `_m` — contrat des tests).
const AVERTISSEMENTS = [[zoneNonConfiguree, 'zone', 'avertissementZoneNonConfiguree'], [rayonServre, 'rayon', 'avertissementRayonServre']];
const CHAMPS_LIEU = [['latitude', 'latitude', 'latitude'], ['longitude', 'longitude', 'longitude'], ['rayon_m', 'rayon', 'rayon']];

// Une chaîne vide coerce à 0 avec `Number()` : elle doit rester invalide, pas
// devenir une coordonnée ou un rayon nul légitimes.
const enNombre = (valeur) => (String(valeur).trim() === '' ? NaN : Number(valeur));

// Liste blanche explicite : `cle` est un artefact d'affichage et ne doit
// jamais partir. `nom` s'écrit depuis Yros/Kavero#369 ; `id` n'accompagne que
// les lieux existants — son absence est exactement ce qui dit à Odoo « celui-ci
// est à créer ».
const aEnvoyer = (lieu) => ({
  ...(lieu.id ? { id: lieu.id } : {}),
  nom: String(lieu.nom ?? '').trim(),
  latitude: enNombre(lieu.latitude), longitude: enNombre(lieu.longitude), rayon_m: enNombre(lieu.rayon_m),
  // Yros/Kavero#491 : chaque lieu choisit son contrôle, « selon la société » par défaut.
  mode: lieu.mode || 'societe',
});

// Un lieu sans libellé est indistinguable des autres dans la liste : le nom
// est aussi obligatoire qu'une coordonnée.
const nomValide = (nom) => {
  const detoure = String(nom ?? '').trim();
  return detoure.length > 0 && detoure.length <= LONGUEUR_NOM_MAX;
};

// `cle` : identité stable d'une ligne du formulaire, `id` pour un lieu déjà en
// base et un jeton local pour un lieu qui n'existe pas encore. Elle vaut
// `String(id)` sur l'existant, donc les `data-testid` des lieux chargés ne
// bougent pas.
const avecCle = (lieux) => (lieux || []).map((lieu) => ({ ...lieu, cle: String(lieu.id) }));

// Les trois valeurs d'un lieu vont ensemble. Le plafond `RAYON_ZONE_MAX_M`
// d'Odoo n'est pas recopié (constante d'implémentation absente de C13) : un
// rayon démesuré part et revient en 409 plutôt que de diverger du proxy.
const champsValides = (lieux) =>
  lieux.every((lieu) => {
    const latitude = enNombre(lieu.latitude), longitude = enNombre(lieu.longitude), rayon = enNombre(lieu.rayon_m);
    return nomValide(lieu.nom)
      && Number.isFinite(latitude) && latitude >= -90 && latitude <= 90
      && Number.isFinite(longitude) && longitude >= -180 && longitude <= 180
      && Number.isInteger(rayon) && rayon >= 0;
  });

// Contre l'erreur de signe : le seul endroit du produit qui la ferait voir,
// une inversion déplaçant un site de milliers de kilomètres sans ça. Les
// lettres passent par `t()` : Nord/Sud/Est sont identiques dans les deux
// langues, mais Ouest ≠ West — un gestionnaire anglophone lisant « O » n'y
// comprendrait rien.
const echoHemisphere = (latitude, longitude, t) => {
  const lat = enNombre(latitude), lon = enNombre(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return '';
  const nordSud = lat < 0 ? t('configurationPointage.sud') : t('configurationPointage.nord');
  const estOuest = lon < 0 ? t('configurationPointage.ouest') : t('configurationPointage.est');
  return `${Math.abs(lat)} ${nordSud} · ${Math.abs(lon)} ${estOuest}`;
};

// Copie du patron `Pointage.js` (bolt 030, non fusionné — code non partagé,
// motif au plan § Risques) : promesse qui ne rejette jamais, minuterie propre
// en plus de l'option `timeout`. Issue différente : un échec rend `null` et
// l'écran le dit, il ne l'avale pas — la RH configure, elle ne pointe pas.
const lirePosition = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) { resolve(null); return; }
    let tranche = false;
    const trancher = (valeur) => { if (!tranche) { tranche = true; resolve(valeur); } };
    const minuterie = setTimeout(() => trancher(null), DELAI_POSITION_MS);
    navigator.geolocation.getCurrentPosition(
      (p) => { clearTimeout(minuterie); trancher({ latitude: p.coords.latitude, longitude: p.coords.longitude }); },
      () => { clearTimeout(minuterie); trancher(null); },
      // Mêmes exigences que l'écran du salarié (`Espace/Pointage.js`), et pour
      // une raison plus forte : c'est ICI qu'on définit le point de référence
      // auquel tous les pointages seront comparés. `maximumAge: 0` interdit la
      // position en cache — le navigateur en rend volontiers une d'il y a une
      // heure, prise ailleurs, et le site se retrouverait posé au mauvais
      // endroit. Un point de référence faux marque tout le monde hors zone.
      { enableHighAccuracy: true, timeout: DELAI_POSITION_MS, maximumAge: 0 }
    );
  });

// Le statut d'abord, jamais la présence d'un message : un 502 porte lui aussi
// un corps, mais dans `error`, jamais un refus métier C13. `enveloppe.message`
// d'abord, repli i18n par `motif` ensuite ; un motif hors contrat ne se
// replie sur aucun des deux connus.
const REPLI_PAR_MOTIF = {
  non_autorise: 'configurationPointage.refusNonAutorise',
  valeurs_invalides: 'configurationPointage.refusValeursInvalides',
};
const interpreterProbleme = (erreur, t) => {
  const enveloppe = erreur?.response?.status === 409 ? erreur.response.data : null;
  if (!enveloppe) return t('configurationPointage.erreurTechnique');
  const cleDeRepli = REPLI_PAR_MOTIF[enveloppe.motif];
  return enveloppe.message || (cleDeRepli ? t(cleDeRepli) : t('configurationPointage.erreurTechnique'));
};

// Le géocodage a ses propres issues, distinctes du refus métier C13 : 503 dit
// « la recherche n'est pas activée ici », 502 dit « le service est tombé ».
// Les deux sont des impasses, mais la première ne se réessaie pas — d'où deux
// phrases, et pas un « erreur technique » commun qui ferait recliquer en vain.
const CLE_PAR_STATUT_GEOCODAGE = {
  503: 'configurationPointage.adresseNonConfiguree',
  502: 'configurationPointage.adresseErreur',
};
const interpreterGeocodage = (erreur, t) =>
  t(CLE_PAR_STATUT_GEOCODAGE[erreur?.response?.status] || 'configurationPointage.adresseErreur');


function ConfigurationPointage() {
  const { t } = useTranslation();
  const [chargement, setChargement] = useState(true);
  const [erreurChargement, setErreurChargement] = useState(null);
  const [mode, setMode] = useState('desactive');
  const [lieux, setLieux] = useState([]);
  // Ids des lieux retirés de l'écran mais pas encore de la base : ils ne
  // partent qu'à l'enregistrement, comme les modifications. Retirer une ligne
  // n'écrit rien tant que la RH n'a pas validé.
  const [supprimes, setSupprimes] = useState([]);
  const [enCours, setEnCours] = useState(false);
  const [erreurEnvoi, setErreurEnvoi] = useState(null);
  const [lieuEnLocalisation, setLieuEnLocalisation] = useState(null);
  // Lieu que le prochain clic sur la carte déplacera. Aucun par défaut : un
  // clic sur la carte ne doit jamais déplacer un site que la RH n'a pas
  // désigné.
  const [cleActive, setCleActive] = useState(null);
  // Demande de recadrage de la carte, émise par « utiliser ma position » et
  // par elle seule. Un jeton plutôt qu'un booléen : deux relevés successifs au
  // même endroit doivent recadrer les deux fois.
  const [recentrage, setRecentrage] = useState(null);
  // Adresses saisies, par lieu. Elles ne sont ni chargées ni enregistrées :
  // l'adresse sert une fois, à obtenir des coordonnées, et ce sont elles seules
  // qu'Odoo connaît (contrat C17). Un état à part plutôt qu'un champ de `lieux`
  // rend cette frontière visible — `aEnvoyer` ne peut pas l'emporter par
  // inadvertance.
  const [adresses, setAdresses] = useState({});
  // Issue de la dernière recherche, pour un seul lieu à la fois : la RH en
  // configure un, pas cinq en parallèle.
  const [geocodage, setGeocodage] = useState(null);
  // Lieu dont l'affiche QR est ouverte, et l'issue de la dernière
  // régénération demandée depuis cette affiche (C25).
  const [cleAffiche, setCleAffiche] = useState(null);
  const [regeneration, setRegeneration] = useState(null);
  const compteurRecentrage = useRef(0);
  const compteurNouveaux = useRef(0);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreurChargement(null);
    try {
      const reponse = await apiService.getConfigurationPointage();
      setMode(reponse.mode);
      setLieux(avecCle(reponse.lieux));
      setSupprimes([]);
    } catch (erreur) {
      // Échec de la lecture initiale ⇒ aucun formulaire : un formulaire vide
      // laisserait la RH écraser une configuration réelle par `desactive` et
      // des zones nulles.
      setErreurChargement(interpreterProbleme(erreur, t));
    } finally {
      setChargement(false);
    }
  }, [t]);

  useEffect(() => {
    charger();
  }, [charger]);

  const modifierLieu = (cle, champ, valeur) => {
    setLieux((precedent) => precedent.map((lieu) => (lieu.cle === cle ? { ...lieu, [champ]: valeur } : lieu)));
  };

  const ajouterLieu = () => {
    compteurNouveaux.current += 1;
    const cle = `nouveau-${compteurNouveaux.current}`;
    // Champs vides et non préremplis : une coordonnée par défaut serait un
    // site réel posé au large du golfe de Guinée, que la RH pourrait
    // enregistrer sans s'en apercevoir.
    setLieux((precedent) => [...precedent, { cle, id: null, nom: '', latitude: '', longitude: '', rayon_m: '', mode: 'societe' }]);
  };

  const retirerLieu = (cle) => {
    setLieux((precedent) => {
      const vise = precedent.find((lieu) => lieu.cle === cle);
      // Un lieu jamais enregistré disparaît sans rien à demander à Odoo.
      if (vise && vise.id) { setSupprimes((ids) => [...ids, vise.id]); }
      return precedent.filter((lieu) => lieu.cle !== cle);
    });
    // Un lieu retiré ne peut plus être la cible du prochain clic : sinon le
    // clic suivant écrirait dans une ligne qui n'existe plus.
    setCleActive((actuelle) => (actuelle === cle ? null : actuelle));
  };

  // Un seul chemin pour poser des coordonnées, qu'elles viennent du GPS ou
  // d'un clic sur la carte : les deux valeurs vont ensemble, et les écrire en
  // deux temps laisserait un instant où le lieu porte une demi-position.
  const poserCoordonnees = useCallback((cle, position) => {
    setLieux((precedent) =>
      precedent.map((lieu) => (lieu.cle === cle ? { ...lieu, latitude: position.latitude, longitude: position.longitude } : lieu))
    );
  }, []);

  // Recadrer après avoir posé un point vaut pour le GPS comme pour l'adresse,
  // et pour la même raison : sans ça le marqueur part où il doit, mais hors
  // écran, et la RH en conclut que le relevé est faux.
  const poserEtRecadrer = useCallback((cle, position) => {
    poserCoordonnees(cle, position);
    compteurRecentrage.current += 1;
    setRecentrage({ cle, jeton: compteurRecentrage.current });
  }, [poserCoordonnees]);

  const utiliserPosition = async (cle) => {
    setLieuEnLocalisation(cle);
    const position = await lirePosition();
    setLieuEnLocalisation(null);
    if (!position) { notify(t('configurationPointage.positionIndisponible'), '', 3); return; }
    // Un clic sur la carte ne recadre pas, lui — l'utilisateur regarde déjà
    // l'endroit qu'il a désigné.
    poserEtRecadrer(cle, position);
  };

  const chercherAdresse = async (cle) => {
    const adresse = String(adresses[cle] ?? '').trim();
    // Une adresse vide ne part pas : le service refuserait, et un aller-retour
    // pour un refus que le front sait prononcer est une seconde perdue.
    if (!adresse) return;

    setGeocodage({ cle, etat: 'encours', resultats: [] });
    try {
      const reponse = await apiService.geocoderAdresse(adresse);
      const resultats = Array.isArray(reponse?.resultats) ? reponse.resultats : [];
      // Un seul résultat ne mérite pas un choix : on pose, la carte suit.
      if (resultats.length === 1) {
        poserEtRecadrer(cle, resultats[0]);
        setGeocodage(null);
        return;
      }
      setGeocodage({ cle, etat: resultats.length ? 'resultats' : 'vide', resultats });
    } catch (erreur) {
      setGeocodage({ cle, etat: 'erreur', resultats: [], message: interpreterGeocodage(erreur, t) });
    }
  };

  const retenirResultat = (cle, resultat) => {
    poserEtRecadrer(cle, resultat);
    setGeocodage(null);
  };

  const ouvrirAffiche = (cle) => {
    setCleAffiche(cle);
    setRegeneration(null);
  };

  // Le nouveau jeton remplace l'ancien dans l'état de l'écran, sans relire
  // toute la configuration : une relecture écraserait les champs que la RH
  // est peut-être en train de modifier.
  const regenererQr = async (lieu) => {
    setRegeneration({ etat: 'encours' });
    try {
      const reponse = await apiService.regenererQrLieu(lieu.id);
      setLieux((precedent) => precedent.map((autre) => (
        autre.cle === lieu.cle ? { ...autre, jeton_qr: reponse.jeton_qr } : autre
      )));
      setRegeneration({ etat: 'succes' });
    } catch (erreur) {
      setRegeneration({ etat: 'erreur', message: interpreterProbleme(erreur, t) });
    }
  };

  const enregistrer = async (event) => {
    event.preventDefault();
    if (enCours) return;
    if (lieux.some((lieu) => !nomValide(lieu.nom))) { setErreurEnvoi(t('configurationPointage.nomRequis')); return; }
    if (!champsValides(lieux)) { setErreurEnvoi(t('configurationPointage.champsInvalides')); return; }

    setEnCours(true);
    setErreurEnvoi(null);
    try {
      const reponse = await apiService.enregistrerConfigurationPointage({
        mode,
        lieux: [...lieux.map(aEnvoyer), ...supprimes.map((id) => ({ id, supprimer: true }))],
      });
      // La configuration relue en est la preuve durable : pas de message de
      // succès dupliqué à côté, une information à un seul endroit.
      setMode(reponse.mode);
      setLieux(avecCle(reponse.lieux));
      setSupprimes([]);
      notify(t('configurationPointage.succes'), '', 2);
    } catch (erreur) {
      setErreurEnvoi(interpreterProbleme(erreur, t));
    } finally {
      setEnCours(false);
    }
  };

  const lieuAffiche = lieux.find((lieu) => lieu.cle === cleAffiche && lieu.jeton_qr);
  const lieuxFautifs = lieux.filter(zoneNonConfiguree);
  const bannierePersonneVisible = mode === 'strict' && lieux.length === 0;
  const banniereLieuxVisible = mode === 'strict' && lieux.length > 0 && lieuxFautifs.length > 0;

  return (
    <div className="p-4">
      <HeaderCard title={t('configurationPointage.titre')} text={t('configurationPointage.description')} />

      {chargement ? (
        <LoadingBarWithStyles />
      ) : erreurChargement ? (
        <p data-testid="configuration-pointage-erreur-chargement" className={CLASSE_ERREUR}>{erreurChargement}</p>
      ) : (
        <form data-testid="configuration-pointage-formulaire" onSubmit={enregistrer} className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-slate-100 space-y-4">
          {(bannierePersonneVisible || banniereLieuxVisible) && (
            <div data-testid="configuration-pointage-banniere-strict" className={CLASSE_AVERTISSEMENT}>
              {bannierePersonneVisible ? t('configurationPointage.bannierePersonne') : t('configurationPointage.banniereLieux', { lieux: lieuxFautifs.map((lieu) => lieu.nom).join(', ') })}
            </div>
          )}

          <label className="block text-sm font-medium">
            {t('configurationPointage.modeSociete')}
            <select data-testid="configuration-pointage-mode" value={mode} onChange={(e) => setMode(e.target.value)} className={CLASSE_CHAMP}>
              {MODES.map((code) => (
                <option key={code} value={code}>{t(`configurationPointage.modes.${code}`)}</option>
              ))}
            </select>
          </label>

          {lieux.length === 0 && (
            <p data-testid="configuration-pointage-aucun-lieu" className={CLASSE_INFO}>
              {t('configurationPointage.aucunLieu')}
            </p>
          )}

          {lieux.map((lieu) => (
            <div key={lieu.cle} data-testid={`configuration-pointage-lieu-${lieu.cle}`} className="border border-slate-100 rounded-xl p-3 space-y-2">
              <div className="flex items-end gap-3">
                <label className="block text-sm font-medium flex-1">
                  {t('configurationPointage.nom')}
                  <input type="text" data-testid={`configuration-pointage-nom-${lieu.cle}`} value={lieu.nom} onChange={(e) => modifierLieu(lieu.cle, 'nom', e.target.value)} className={CLASSE_CHAMP} />
                </label>
                <label className="block text-sm font-medium flex-1">
                  {t('configurationPointage.modeLieu')}
                  <select data-testid={`configuration-pointage-mode-${lieu.cle}`} value={lieu.mode || 'societe'}
                    onChange={(e) => modifierLieu(lieu.cle, 'mode', e.target.value)} className={CLASSE_CHAMP}>
                    <option value="societe">{t('configurationPointage.selonSociete', { mode: t(`configurationPointage.modesCourts.${mode}`) })}</option>
                    {MODES.map((code) => (
                      <option key={code} value={code}>{t(`configurationPointage.modesCourts.${code}`)}</option>
                    ))}
                  </select>
                </label>
                {/* Pas de jeton, pas d'affiche : un lieu pas encore enregistré
                    n'en a pas, il naît à la relecture de la configuration. */}
                {lieu.id && lieu.jeton_qr && (
                  <button type="button" data-testid={`configuration-pointage-qr-${lieu.cle}`} onClick={() => ouvrirAffiche(lieu.cle)} className="px-3 py-2 text-xs font-medium rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50">
                    {t('configurationPointage.qrCode')}
                  </button>
                )}
                <button type="button" data-testid={`configuration-pointage-supprimer-${lieu.cle}`} onClick={() => retirerLieu(lieu.cle)} className="px-3 py-2 text-xs font-medium rounded-lg border border-red-200 text-red-700 hover:bg-red-50">
                  {t('configurationPointage.supprimerLieu')}
                </button>
              </div>

              {AVERTISSEMENTS.filter(([predicat]) => predicat(lieu)).map(([, suffixe, cle]) => (
                <p key={suffixe} data-testid={`configuration-pointage-avertissement-${suffixe}-${lieu.cle}`} className={CLASSE_AVERTISSEMENT}>{t(`configurationPointage.${cle}`)}</p>
              ))}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {CHAMPS_LIEU.map(([champ, suffixe, cle]) => (
                  <label key={champ} className="block text-sm font-medium">
                    {t(`configurationPointage.${cle}`)}
                    <input type="text" data-testid={`configuration-pointage-${suffixe}-${lieu.cle}`} value={lieu[champ]} onChange={(e) => modifierLieu(lieu.cle, champ, e.target.value)} className={CLASSE_CHAMP} />
                  </label>
                ))}
              </div>

              <p data-testid={`configuration-pointage-echo-${lieu.cle}`} className="text-xs text-gray-500">{echoHemisphere(lieu.latitude, lieu.longitude, t)}</p>

              {/* La saisie d'adresse ne remplace pas les trois champs, elle les
                  remplit : la RH voit toujours ce qui sera enregistré, et peut
                  corriger à la main. L'adresse elle-même ne part jamais. */}
              <div className="flex flex-col sm:flex-row sm:items-end gap-2">
                <label className="block text-sm font-medium flex-1">
                  {t('configurationPointage.adresse')}
                  <input
                    type="text"
                    data-testid={`configuration-pointage-adresse-${lieu.cle}`}
                    value={adresses[lieu.cle] ?? ''}
                    placeholder={t('configurationPointage.adressePlaceholder')}
                    onChange={(e) => setAdresses((precedent) => ({ ...precedent, [lieu.cle]: e.target.value }))}
                    className={CLASSE_CHAMP}
                  />
                </label>
                <button
                  type="button"
                  data-testid={`configuration-pointage-chercher-adresse-${lieu.cle}`}
                  onClick={() => chercherAdresse(lieu.cle)}
                  disabled={geocodage?.cle === lieu.cle && geocodage.etat === 'encours'}
                  className="px-3 py-2 text-xs font-medium rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  {geocodage?.cle === lieu.cle && geocodage.etat === 'encours'
                    ? t('configurationPointage.rechercheEnCours')
                    : t('configurationPointage.chercherAdresse')}
                </button>
              </div>

              {geocodage?.cle === lieu.cle && geocodage.etat === 'vide' && (
                <p data-testid={`configuration-pointage-adresse-vide-${lieu.cle}`} className={CLASSE_INFO}>
                  {t('configurationPointage.adresseIntrouvable')}
                </p>
              )}

              {geocodage?.cle === lieu.cle && geocodage.etat === 'erreur' && (
                <p data-testid={`configuration-pointage-adresse-erreur-${lieu.cle}`} className={CLASSE_ERREUR}>
                  {geocodage.message}
                </p>
              )}

              {geocodage?.cle === lieu.cle && geocodage.etat === 'resultats' && (
                <div data-testid={`configuration-pointage-adresse-resultats-${lieu.cle}`} className="space-y-1">
                  <p className="text-xs text-gray-500">{t('configurationPointage.resultatsAdresse')}</p>
                  {geocodage.resultats.map((resultat, rang) => (
                    <div key={`${resultat.latitude}-${resultat.longitude}-${rang}`} className="flex items-center gap-2">
                      <span className="text-xs text-slate-700 flex-1">{resultat.libelle}</span>
                      <button
                        type="button"
                        data-testid={`configuration-pointage-adresse-resultat-${lieu.cle}-${rang}`}
                        onClick={() => retenirResultat(lieu.cle, resultat)}
                        className="px-2 py-1 text-xs font-medium rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50"
                      >
                        {t('configurationPointage.utiliserCeResultat')}
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <button type="button" data-testid={`configuration-pointage-position-${lieu.cle}`} onClick={() => utiliserPosition(lieu.cle)} disabled={lieuEnLocalisation === lieu.cle} className="px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                  {t('configurationPointage.utiliserPosition')}
                </button>
                <button type="button" data-testid={`configuration-pointage-placer-${lieu.cle}`} onClick={() => setCleActive(lieu.cle)} aria-pressed={cleActive === lieu.cle} className={`px-3 py-1.5 text-xs font-medium rounded-lg border ${cleActive === lieu.cle ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}>
                  {t('configurationPointage.placerSurCarte')}
                </button>
              </div>
            </div>
          ))}

          <button type="button" data-testid="configuration-pointage-ajouter" onClick={ajouterLieu} className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50">
            {t('configurationPointage.ajouterLieu')}
          </button>

          <CarteLieux lieux={lieux} cleActive={cleActive} onPosition={poserCoordonnees} recentrage={recentrage} />

          {erreurEnvoi && (
            <p data-testid="configuration-pointage-erreur-envoi" className={CLASSE_ERREUR}>{erreurEnvoi}</p>
          )}

          <div className="flex justify-end pt-2">
            <button type="submit" data-testid="configuration-pointage-enregistrer" disabled={enCours} className="flex items-center gap-2 px-6 py-3 bg-[--color-primary] text-white rounded-lg hover:bg-[--bg-hover] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200">
              {enCours ? t('configurationPointage.enregistrementEnCours') : t('configurationPointage.enregistrer')}
            </button>
          </div>
        </form>
      )}

      {/* Hors du formulaire : aucun bouton de l'affiche ne doit pouvoir
          l'enregistrer. */}
      {lieuAffiche && (
        <AfficheQrLieu
          lieu={lieuAffiche}
          societe={localStorage.getItem('companyName') || ''}
          regeneration={regeneration}
          onRegenerer={() => regenererQr(lieuAffiche)}
          onFermer={() => setCleAffiche(null)}
        />
      )}

      {/* Les horaires de la société vivent sur le même écran que le contrôle
          de zone — tout ce qui règle le pointage au même endroit —, mais ils
          se chargent et s'enregistrent seuls : un refus sur l'un ne fait pas
          perdre la saisie de l'autre. */}
      <HorairesTravail />
    </div>
  );
}

export default ConfigurationPointage;
